import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Download, TrendingUp, Music, Heart, Play,
  Zap, BarChart3, Users, RefreshCw
} from 'lucide-react';
import { API_URL } from '../utils/api';

const AdminAnalytics = () => {
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, isAdmin, authLoading, navigate]);
  
  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchAnalytics();
    }
  }, [isAuthenticated, isAdmin]);
  
  const handleExport = () => {
    window.open(`${API_URL}/api/admin/analytics/export`, '_blank');
  };
  
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div data-testid="analytics-page" className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-xl">Impact Analytics</h1>
              <p className="text-xs text-muted-foreground">
                {analytics?.start_date} to {analytics?.end_date}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchAnalytics}
              className="rounded-full"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Button
              data-testid="export-btn"
              onClick={handleExport}
              className="rounded-full bg-primary hover:bg-primary/90 text-black"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Impact Snapshot
            </Button>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={<Users className="w-5 h-5" />}
            label="Sessions"
            value={analytics?.total_sessions || 0}
            color="primary"
          />
          <StatCard
            icon={<Play className="w-5 h-5" />}
            label="Previews"
            value={analytics?.total_previews || 0}
            color="accent"
          />
          <StatCard
            icon={<Download className="w-5 h-5" />}
            label="Downloads"
            value={analytics?.total_downloads || 0}
            color="secondary"
          />
          <StatCard
            icon={<Heart className="w-5 h-5" />}
            label="Favorites"
            value={analytics?.total_favorites || 0}
            color="destructive"
          />
        </div>
        
        {/* Insights Section */}
        {analytics?.insights?.length > 0 && (
          <section className="rounded-3xl bg-card/50 border border-white/10 p-6">
            <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              What This Data Suggests
            </h2>
            <div className="space-y-4">
              {analytics.insights.map(insight => (
                <div key={insight.id} className="p-4 rounded-2xl bg-white/5">
                  <h3 className="font-semibold mb-1">
                    {insight.emoji} {insight.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">{insight.text}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4 italic">
              These metrics represent engagement and exploration signals, not clinical or diagnostic outcomes. 
              Data is collected anonymously and is intended to inform program design, facilitation, and creative learning experiences.
            </p>
          </section>
        )}
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Mood Usage */}
          <section className="rounded-3xl bg-card/50 border border-white/10 p-6">
            <h2 className="font-heading font-bold text-lg mb-4">Mood Category Usage</h2>
            <div className="space-y-3">
              {analytics?.mood_usage?.sort((a, b) => (b.preview_count || 0) - (a.preview_count || 0)).slice(0, 8).map(mood => (
                <div key={mood.mood_category} className="flex items-center justify-between">
                  <span className="text-sm">{mood.mood_category}</span>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span title="Previews"><Play className="w-3 h-3 inline mr-1" />{mood.preview_count || 0}</span>
                    <span title="Downloads"><Download className="w-3 h-3 inline mr-1" />{mood.download_count || 0}</span>
                  </div>
                </div>
              ))}
              {(!analytics?.mood_usage || analytics.mood_usage.length === 0) && (
                <p className="text-muted-foreground text-sm">No data yet</p>
              )}
            </div>
          </section>
          
          {/* Vibe Presets */}
          <section className="rounded-3xl bg-card/50 border border-white/10 p-6">
            <h2 className="font-heading font-bold text-lg mb-4">Choose Your Vibe Clicks</h2>
            <div className="space-y-3">
              {analytics?.vibe_presets?.sort((a, b) => (b.click_count || 0) - (a.click_count || 0)).map(preset => (
                <div key={preset.preset_name} className="flex items-center justify-between">
                  <span className="text-sm">{preset.preset_name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{preset.click_count || 0} clicks</span>
                  </div>
                </div>
              ))}
              {(!analytics?.vibe_presets || analytics.vibe_presets.length === 0) && (
                <p className="text-muted-foreground text-sm">No data yet</p>
              )}
            </div>
          </section>
          
          {/* Style Usage */}
          <section className="rounded-3xl bg-card/50 border border-white/10 p-6">
            <h2 className="font-heading font-bold text-lg mb-4">Style / Genre Engagement</h2>
            <div className="space-y-3">
              {analytics?.style_usage?.sort((a, b) => (b.preview_count || 0) - (a.preview_count || 0)).slice(0, 8).map(style => (
                <div key={style.style_tag} className="flex items-center justify-between">
                  <span className="text-sm">{style.style_tag}</span>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span><Play className="w-3 h-3 inline mr-1" />{style.preview_count || 0}</span>
                    <span><Download className="w-3 h-3 inline mr-1" />{style.download_count || 0}</span>
                  </div>
                </div>
              ))}
              {(!analytics?.style_usage || analytics.style_usage.length === 0) && (
                <p className="text-muted-foreground text-sm">No data yet</p>
              )}
            </div>
          </section>
          
          {/* Energy Distribution */}
          <section className="rounded-3xl bg-card/50 border border-white/10 p-6">
            <h2 className="font-heading font-bold text-lg mb-4">Energy Level Distribution</h2>
            <div className="space-y-3">
              {analytics?.energy_distribution?.map(energy => {
                const total = analytics.energy_distribution.reduce((sum, e) => sum + (e.preview_count || 0), 0);
                const percent = total > 0 ? Math.round((energy.preview_count / total) * 100) : 0;
                return (
                  <div key={energy.energy_band} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span>{energy.energy_band} Energy</span>
                      <span className="text-muted-foreground">{percent}%</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {(!analytics?.energy_distribution || analytics.energy_distribution.length === 0) && (
                <p className="text-muted-foreground text-sm">No data yet</p>
              )}
            </div>
          </section>
        </div>
        
        {/* Session Flow */}
        <section className="rounded-3xl bg-card/50 border border-white/10 p-6">
          <h2 className="font-heading font-bold text-lg mb-4">Session Flow Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-2xl bg-white/5">
              <p className="text-2xl font-bold text-primary">
                {analytics?.session_summary?.avg_previews_per_session || 0}
              </p>
              <p className="text-xs text-muted-foreground">Avg Previews / Session</p>
            </div>
            <div className="text-center p-4 rounded-2xl bg-white/5">
              <p className="text-2xl font-bold text-accent">
                {analytics?.session_summary?.avg_downloads_per_session || 0}
              </p>
              <p className="text-xs text-muted-foreground">Avg Downloads / Session</p>
            </div>
            <div className="text-center p-4 rounded-2xl bg-white/5">
              <p className="text-lg font-bold text-white">
                {analytics?.session_summary?.most_common_first_vibe || 'N/A'}
              </p>
              <p className="text-xs text-muted-foreground">Most Common First Vibe</p>
            </div>
            <div className="text-center p-4 rounded-2xl bg-white/5">
              <p className="text-lg font-bold text-white">
                {analytics?.session_summary?.most_common_last_vibe || 'N/A'}
              </p>
              <p className="text-xs text-muted-foreground">Most Common Last Vibe</p>
            </div>
          </div>
        </section>
        
        {/* Top Loops */}
        <div className="grid md:grid-cols-2 gap-6">
          <section className="rounded-3xl bg-card/50 border border-white/10 p-6">
            <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <Play className="w-5 h-5 text-accent" />
              Top Loops by Preview
            </h2>
            <div className="space-y-3">
              {analytics?.top_loops_preview?.map((loop, i) => (
                <div key={loop.title} className="flex items-center gap-3">
                  <span className="text-muted-foreground text-sm w-6">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{loop.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {loop.mood_category} • {loop.style_tags?.join(', ')}
                    </p>
                  </div>
                  <span className="text-sm text-accent">{loop.preview_count}</span>
                </div>
              ))}
              {(!analytics?.top_loops_preview || analytics.top_loops_preview.length === 0) && (
                <p className="text-muted-foreground text-sm">No data yet</p>
              )}
            </div>
          </section>
          
          <section className="rounded-3xl bg-card/50 border border-white/10 p-6">
            <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <Download className="w-5 h-5 text-secondary" />
              Top Loops by Download
            </h2>
            <div className="space-y-3">
              {analytics?.top_loops_download?.map((loop, i) => (
                <div key={loop.title} className="flex items-center gap-3">
                  <span className="text-muted-foreground text-sm w-6">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{loop.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {loop.mood_category} • {loop.style_tags?.join(', ')}
                    </p>
                  </div>
                  <span className="text-sm text-secondary">{loop.download_count}</span>
                </div>
              ))}
              {(!analytics?.top_loops_download || analytics.top_loops_download.length === 0) && (
                <p className="text-muted-foreground text-sm">No data yet</p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, color }) => {
  const colorClasses = {
    primary: 'text-primary bg-primary/10',
    accent: 'text-accent bg-accent/10',
    secondary: 'text-secondary bg-secondary/10',
    destructive: 'text-destructive bg-destructive/10'
  };
  
  return (
    <div className="rounded-2xl bg-card/50 border border-white/10 p-4">
      <div className={`w-10 h-10 rounded-xl ${colorClasses[color]} flex items-center justify-center mb-3`}>
        {icon}
      </div>
      <p className="text-2xl font-bold">{value.toLocaleString()}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
};

export default AdminAnalytics;
